import { FC, useCallback, useMemo } from 'react';
import { User } from 'types/User';
import { useLogoutUser } from 'modules/app/appSelector';
import UserPermissionGate from 'components/UserPermissionGate';
import { UserPermission } from 'constants/userPermission';
import Link from 'components/shared/Link';
import { Avatar, DropdownMenu, Icon } from '@kontentino/ui';
import { faBell } from '@fortawesome/pro-regular-svg-icons/faBell';
import { faCheck } from '@fortawesome/pro-regular-svg-icons/faCheck';
import { faMobile } from '@fortawesome/pro-regular-svg-icons/faMobile';
import { faPowerOff } from '@fortawesome/pro-regular-svg-icons/faPowerOff';
import { faSpinner } from '@fortawesome/pro-regular-svg-icons/faSpinner';
import { faUser } from '@fortawesome/pro-regular-svg-icons/faUser';
import { faArrowRightToArc } from '@fortawesome/pro-regular-svg-icons/faArrowRightToArc';
import VerticalNavigationDropdownOption from './UserActionDropdownOption';
import { VerticalMenuItemWrapper } from '../VerticalMenuItemWrapper';
import { useTranslation } from 'react-i18next';
import { composeRoute } from 'constants/routes';
import { useAccount } from 'modules/account/accountSelector';
import Skeleton from 'react-loading-skeleton';
import { useConnectedAccountsQuery } from 'app/modules/accountSwitcher/hooks/useConnectedAccountsQuery';
import { useSwitchAccountMutation } from 'app/modules/accountSwitcher/hooks/useSwitchAccountMutation';
import PendingInvitationMenuItems from 'app/modules/accountSwitcher/components/PendingInvitationMenuItems';

type Props = {
  user: Pick<User, 'role' | 'avatar' | 'name'>;
};

const UserActions: FC<Props> = ({ user }) => {
  const logoutUser = useLogoutUser();
  const { t } = useTranslation();
  const { isSubscription } = useAccount();
  const { data, isLoading, isError } = useConnectedAccountsQuery();
  const switchAccountMutation = useSwitchAccountMutation();

  const currentAccount = data?.current_account;
  const availableAccounts = useMemo(
    () => data?.available_accounts ?? [],
    [data?.available_accounts],
  );

  const pendingInvitations = useMemo(
    () => data?.pending_invitations ?? [],
    [data?.pending_invitations],
  );

  const handleSwitchAccount = useCallback(
    (accountId: number) => {
      if (switchAccountMutation.isLoading) return;
      switchAccountMutation.mutate(accountId);
    },
    [switchAccountMutation],
  );

  return useMemo(() => {
    return (
      <VerticalMenuItemWrapper className="tw-mb-4 tw-pl-4">
        <DropdownMenu.Root>
          <DropdownMenu.Trigger
            className="hover:tw-text-primary-100"
            data-name="navbar_avatar"
          >
            <span className="tw-flex tw-h-full tw-w-full tw-cursor-pointer tw-items-center tw-text-inherit tw-transition-all tw-duration-100 tw-ease-in-out">
              <Avatar
                src={user.avatar?.src || undefined}
                size={32}
                name={user.name}
                className="tw-border-grayscale-20 tw-transition-all tw-duration-100 tw-ease-in-out hover:tw-border-2 hover:tw-border-primary-20"
              />
            </span>
          </DropdownMenu.Trigger>
          <DropdownMenu.Portal>
            <DropdownMenu.Content
              className="tw-z-[99999] tw-min-w-[240px]"
              side="right"
              sideOffset={-10}
            >
              <div className="tw-px-3 tw-py-2 tw-text-xs tw-font-medium tw-text-grayscale-100">
                {t('accounts')}
              </div>
              {isLoading ? (
                <div className="tw-px-3 tw-py-2">
                  <Skeleton height={40} borderRadius={8} />
                </div>
              ) : isError ? (
                <div className="tw-px-3 tw-py-2 tw-text-sm tw-text-danger-100">
                  {t('somethingWentWrong')}
                </div>
              ) : (
                <div className="tw-flex tw-max-h-[300px] tw-flex-col tw-gap-1 tw-overflow-y-auto tw-px-2">
                  {currentAccount && (
                    <div
                      className="tw-flex tw-items-center tw-justify-between tw-rounded-lg tw-bg-primary-10 tw-p-2"
                      data-cy="account-switcher_current-account"
                    >
                      <div className="tw-flex tw-flex-col">
                        <span className="tw-text-sm tw-font-medium tw-text-primary-100">
                          {currentAccount.account_name}
                        </span>
                        <span className="tw-text-[10px] tw-text-primary-100">
                          {currentAccount.user_role}
                        </span>
                      </div>
                      <Icon
                        icon={faCheck}
                        className="tw-text-primary-100"
                        size="sm"
                      />
                    </div>
                  )}
                  {availableAccounts.map((account) => {
                    const isSwitching =
                      switchAccountMutation.isLoading &&
                      switchAccountMutation.variables === account.account_id;

                    return (
                      <button
                        key={account.account_id}
                        className="tw-flex tw-w-full tw-cursor-pointer tw-items-center tw-justify-between tw-rounded-lg tw-p-2 tw-text-left hover:tw-bg-grayscale-5"
                        onClick={() => handleSwitchAccount(account.account_id)}
                        disabled={switchAccountMutation.isLoading}
                        data-cy={`account-switcher_item-${account.account_id}`}
                      >
                        <div className="tw-flex tw-flex-col">
                          <span className="tw-text-sm tw-font-medium tw-text-grayscale-180">
                            {account.account_name}
                          </span>
                          <span className="tw-text-[10px] tw-text-grayscale-100">
                            {account.user_role}
                          </span>
                        </div>
                        {isSwitching && (
                          <Icon
                            icon={faSpinner}
                            className="tw-animate-spin tw-text-grayscale-100"
                            size="sm"
                          />
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
              <PendingInvitationMenuItems invitations={pendingInvitations} />
              <DropdownMenu.Separator />
              <Link to={composeRoute('settings.routes.personal')} isRouterLink>
                <DropdownMenu.Item data-name="navbar_avatar_personal">
                  <VerticalNavigationDropdownOption
                    icon={<Icon icon={faUser} />}
                    label={t('navigation.profile.myAccount')}
                  />
                </DropdownMenu.Item>
              </Link>
              <DropdownMenu.Separator />
              <UserPermissionGate scopes={UserPermission.PAGE_CREATE}>
                <Link
                  to={composeRoute('settings.routes.authorization')}
                  isRouterLink
                >
                  <DropdownMenu.Item data-name="navbar_avatar_social-login">
                    <VerticalNavigationDropdownOption
                      icon={
                        <Icon
                          icon={faArrowRightToArc}
                          className="tw-text-grayscale-100"
                        />
                      }
                      label={t('authorization')}
                    />
                  </DropdownMenu.Item>
                </Link>
              </UserPermissionGate>
              <Link to={composeRoute('settings.routes.devices')} isRouterLink>
                <DropdownMenu.Item data-name="navbar_avatar_devices">
                  <VerticalNavigationDropdownOption
                    icon={<Icon icon={faMobile} />}
                    label={t('navigation.profile.myDevices')}
                  />
                </DropdownMenu.Item>
              </Link>
              <Link
                to={composeRoute('settings.routes.notifications')}
                isRouterLink
              >
                <DropdownMenu.Item data-name="navbar_avatar_notifications">
                  <VerticalNavigationDropdownOption
                    icon={<Icon icon={faBell} />}
                    label={t('navigation.profile.notificationSettings')}
                  />
                </DropdownMenu.Item>
              </Link>
              <DropdownMenu.Separator />
              <DropdownMenu.Item
                data-name="navbar_avatar_logout"
                onSelect={logoutUser}
              >
                <VerticalNavigationDropdownOption
                  icon={<Icon icon={faPowerOff} />}
                  label={t('navigation.profile.logout')}
                />
              </DropdownMenu.Item>
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
      </VerticalMenuItemWrapper>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isSubscription,
    t,
    user.avatar?.src,
    user.name,
    isLoading,
    isError,
    currentAccount,
    availableAccounts,
    pendingInvitations,
    switchAccountMutation.isLoading,
    switchAccountMutation.variables,
    handleSwitchAccount,
  ]);
};

export default UserActions;
